import { useMemo } from 'react';
import { addMonths } from 'date-fns';
import { useForm } from 'react-hook-form';
import { BRANDS, defaultCustomer } from 'shared/constants';
import { useTitle } from 'shared/hooks';
import {
  Brands,
  CustomerForm,
  IBasicQuoteForm,
  ProductForm,
  ProductLookup,
  Quote,
  TCustomProductForm,
  UserInfo,
} from 'shared/types';
import { Product, Customer, ICustomProduct } from 'shared/types/quotes';
import { titleCase } from 'shared/utility';
import { BasicQuoteForm } from './basic-quote-form';
import { LookupRecord } from '@price-for-profit/micro-services';

interface NewQuoteProps {
  brand: Brands;
  user: UserInfo;
  isQuoteToCopy?: boolean;
  quoteToCopy?: Quote;
  productsToCopy?: LookupRecord<ProductLookup>[];
}

export function NewQuote({ brand, user, isQuoteToCopy, quoteToCopy, productsToCopy }: NewQuoteProps) {
  useTitle(`${BRANDS[brand]} - New Quote`);

  const isQualityStainless: boolean = useMemo(() => brand === 'qualitystainless', [brand]);
  const defaultExpiration = useMemo(() => addMonths(new Date(), isQualityStainless ? 3 : 1), [isQualityStainless]);

  const defaultQuote: IBasicQuoteForm = {
    id: '',
    outcome: '',
    quoteDocumentation: [],
    quoteFilesToDelete: [],
    expirationDate: defaultExpiration,
    reminderDate: null,
    customer: defaultCustomer,
    customerSearch: '',
    products: [],
    customProducts: [],
    expenses: [],
    orderNotes: [],
    files: undefined,
    assignedSalesRep: {
      value: `${user.firstName} ${user.lastName}`.toUpperCase(),
      key: titleCase(`${user.firstName} ${user.lastName}`),
    },
  };

  const getLookupProduct = (product: Product): LookupRecord<ProductLookup> | undefined => {
    return productsToCopy?.filter(
      pl =>
        pl.brand.toUpperCase() === brand.toString().toUpperCase() &&
        pl.vendorid === product.vendorId &&
        pl.itemid === product.itemId
    )?.[0];
  };

  const getCopiedProducts = (products: Product[] | undefined): ProductForm[] => {
    if (products && products.length > 0) {
      return products?.map(p => {
        const lookupProduct = getLookupProduct(p);
        return {
          isNew: p.isNew,
          noType: p.noType,
          newVendor: p.newVendor,
          editRevisedPrice: p.editRevisedPrice,
          recalculate: p.recalculate,
          name: p.name,
          itemDescriptionAdj: lookupProduct?.itemdescriptionadj,
          concatenatedItem: lookupProduct?.concatenateditem,
          itemId: lookupProduct?.itemid,
          quantity: p.quantity,
          productType: lookupProduct?.producttype,
          vendorName: lookupProduct?.vendorname,
          vendorId: lookupProduct?.vendorid,
          lastCostPaid: Number(lookupProduct?.lastcostpaid),
          marginAtList: Number(lookupProduct?.marginatlist),
          marginPercentileEighty: Number(lookupProduct?.marginpercentileeighty),
          listPrice: Number(lookupProduct?.listprice),
          isCustomerVendorContracted: p.isCustomerVendorContracted,
          outOfScope: p.outOfScope,
          salesRepAdjustedMargin: p.salesRepAdjustedMargin,
          productGuid: p.productGuid,
          calculationId: p.calculationId,
          quantityBreakFlag: lookupProduct?.quantitybreakflag ?? false,
          extendedDesc: p.extendedDesc,
          costEffectiveDate: lookupProduct?.costeffectivedate,
          listPriceEffectiveDate: lookupProduct?.listpriceeffectivedate,
          unitOfMeasure: lookupProduct?.unitofmeasure,
          MROvsCapital: lookupProduct?.mrovscapital,
          factorSheetName: lookupProduct?.factorsheetname,
          exchangerate: p.exchangerate,
          freightadder: p.freightadder,
          currencytype: p.currencytype,
          customerProductNumber: p.customerProductNumber,
          costDate: lookupProduct?.costdate,
        } as ProductForm;
      });
    }
    return [];
  };

  const getCopiedCustomProducts = (customProducts: ICustomProduct[] | undefined): TCustomProductForm[] => {
    if (customProducts && customProducts.length > 0) {
      return customProducts?.map(
        c =>
          ({
            customProductName: c.customProductName,
            customProductPrice: c.customProductPrice,
            customProductQuantity: c.customProductQuantity,
          } as TCustomProductForm)
      );
    }
    return [];
  };

  const getCopiedCustomer = (customer: Customer | undefined, paymentTerms?: string): CustomerForm => {
    if (customer) {
      return {
        isNew: customer.isNew,
        emptyType: customer.emptyType,
        isExcluded: customer.isExcluded,
        name: customer.name,
        corpNameCustomerName: customer.corpNameCustomerName,
        customerNumber: customer.customerNumber,
        customerNumberAdjusted: customer.customerNumberAdjusted,
        customerType: customer.customerType,
        competition: customer.competition,
        companyId: customer.companyId,
        customerShipTo: customer.customerShipTo,
        paymentTerms,
        assignedSalesReps: customer.assignedSalesReps,
        assignedSalesRepsEmail: customer.assignedSalesRepsEmail,
      } as CustomerForm;
    }
    return defaultCustomer;
  };

  let copiedQuote: IBasicQuoteForm = {
    ...defaultQuote,
    quoteDocumentation: quoteToCopy?.quoteDocumentation || [],
    customer: getCopiedCustomer(quoteToCopy?.customer, quoteToCopy?.pdfInfo.paymentTerms),
    customerSearch: quoteToCopy?.customerSearch || '',
    products: getCopiedProducts(quoteToCopy?.products),
    customProducts: getCopiedCustomProducts(quoteToCopy?.customProducts),
    expenses: quoteToCopy?.expenses || [],
  };

  const methods = useForm<IBasicQuoteForm>({
    defaultValues: isQuoteToCopy ? copiedQuote : defaultQuote,
  });

  return <BasicQuoteForm methods={methods} brand={brand} user={user} />;
}
